import { useRef, useState, useEffect } from "react";

import { Search } from "lucide-react";
import { UserSearchItem } from "src/components";
import { useStoreUser } from "src/hooks";
import { useDebounce } from "src/libs/hooks";
import { AxiosError } from "axios";

interface UserSearchProps {
	value?: string;
	onChange: (user: Partial<StoreUser>) => void;
}

const UserSearch = ({ value, onChange }: UserSearchProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const [debouncedSearchValue, setDebouncedSearchValue] = useState<
		string | undefined
	>();

	const listInnerRef = useRef<HTMLDivElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);

	const {
		data,
		isError,
		error,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage
	} = useStoreUser(debouncedSearchValue, {
		enabled: Boolean(debouncedSearchValue)
	});

	useDebounce(() => setDebouncedSearchValue(value), 300, [value]);

	useEffect(() => {
		if (debouncedSearchValue) {
			setIsOpen(true);
		}
	}, [debouncedSearchValue, data]);

	useEffect(() => {
		const handleOutsideClick = (e: MouseEvent) => {
			if (
				containerRef.current &&
				!containerRef.current.contains(e.target as Node)
			) {
				setIsOpen(false);
			}
		};
		document.addEventListener("mousedown", handleOutsideClick);
		return () => document.removeEventListener("mousedown", handleOutsideClick);
	}, []);

	const handleScroll = () => {
		if (listInnerRef.current) {
			const { scrollTop, scrollHeight, clientHeight } = listInnerRef.current;
			if (scrollTop + clientHeight === scrollHeight) {
				console.log("reached bottom");
				hasNextPage && fetchNextPage();
			}
		}
	};

	const users = data ? data?.pages.flatMap(({ results }) => results) : [];

	if (isError && error)
		return <p className="text-red-600">{(error as AxiosError).message}</p>;

	return (
		<div ref={containerRef} className="relative">
			<div className="relative">
				<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
				<input
					type="text"
					placeholder="Search by employee name..."
					value={value}
					onChange={({ target }: React.ChangeEvent<HTMLInputElement>) =>
						onChange({ name: target.value })
					}
					onFocus={() => debouncedSearchValue && setIsOpen(true)}
					className="w-full bg-white border border-border rounded-lg pl-9 pr-10 py-2 text-sm shadow-xs focus:outline-none focus:ring-2 focus:ring-ring transition-colors"
					aria-label="Search by employee name"
				/>

				{isFetchingNextPage && (
					<span className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin block" />
				)}
			</div>

			{isOpen && users.length > 0 && (
				<div
					ref={listInnerRef}
					onScroll={handleScroll}
					aria-label="List of retail store users"
					className="absolute z-10 mt-1 bg-white border border-border rounded-xl shadow-md max-h-60 overflow-y-auto w-full"
				>
					{users.map((user) => (
						<button
							key={`option-${user.uuid}`}
							type="button"
							className="w-full text-left px-3 py-2.5 text-sm text-foreground hover:bg-primary-lighter transition-colors first:rounded-t-xl last:rounded-b-xl"
							onClick={() => {
								onChange(user);
								setIsOpen(false);
							}}
						>
							<UserSearchItem user={user} icon={null} />
						</button>
					))}
				</div>
			)}
		</div>
	);
};

export default UserSearch;
